import type { DashboardSopSection } from "../types";

export const DASHBOARD_SOP_SECTIONS: DashboardSopSection[] = [
  {
    id: "before-you-start",
    titleKey: "dashboard-sop-before-start-title",
    titleFallback: "Before you start",
    introKey: "dashboard-sop-before-start-intro",
    introFallback:
      "The dashboard gives you a quick picture of how your business is doing. You can check sales, stock, people, and subscriptions without opening each area one by one.",
    steps: [
      {
        bodyKey: "dashboard-sop-before-start-1",
        bodyFallback:
          "Only company admins and store managers can see dashboard data. Other roles, such as inventory clerks or analysts, will not see these numbers."
      },
      {
        bodyKey: "dashboard-sop-before-start-2",
        bodyFallback:
          "Company admins can view all stores or pick one store at a time. Store managers usually see data for their own store only."
      },
      {
        bodyKey: "dashboard-sop-before-start-3",
        bodyFallback:
          "Sign in with an account that has the right role. If you land on a different page after login, ask your company admin to check your access."
      }
    ]
  },
  {
    id: "open-dashboard",
    titleKey: "dashboard-sop-open-title",
    titleFallback: "Open the dashboard",
    introKey: "dashboard-sop-open-intro",
    introFallback:
      "The dashboard is your home screen after you sign in to the portal. It is also called Overview in the left menu.",
    steps: [
      {
        bodyKey: "dashboard-sop-open-1",
        bodyFallback: "After you sign in, open Overview from the left menu."
      },
      {
        bodyKey: "dashboard-sop-open-2",
        bodyFallback:
          "The page title reads Dashboard with the subtitle Overview of your business metrics."
      },
      {
        bodyKey: "dashboard-sop-open-3",
        bodyFallback:
          "At the top you will see filters for store and date range. Below that are summary cards and expandable sections for orders, products, and more."
      }
    ]
  },
  {
    id: "choose-store",
    titleKey: "dashboard-sop-store-title",
    titleFallback: "Choose a store (company admin only)",
    introKey: "dashboard-sop-store-intro",
    introFallback:
      "Company admins can switch between all stores or focus on one branch. Store managers do not use this filter because they already see only their own store.",
    steps: [
      {
        bodyKey: "dashboard-sop-store-1",
        bodyFallback:
          "If you are a company admin, use the Store dropdown at the top right of the dashboard."
      },
      {
        bodyKey: "dashboard-sop-store-2",
        bodyFallback:
          "Pick All stores to see company-wide totals, or pick one store to focus on that location only.",
        imageId: "store-selector"
      },
      {
        bodyKey: "dashboard-sop-store-3",
        bodyFallback:
          "All cards and charts below will update to match the store you selected."
      },
      {
        bodyKey: "dashboard-sop-store-4",
        bodyFallback:
          "If you do not see the Store dropdown, you are signed in as a store manager or another role. Your dashboard already shows the correct store for you."
      }
    ]
  },
  {
    id: "choose-date-range",
    titleKey: "dashboard-sop-date-title",
    titleFallback: "Choose a date range",
    introKey: "dashboard-sop-date-intro",
    introFallback:
      "Use the date filter when you want to review a specific week, month, or custom period. Every number on the dashboard respects the dates you pick.",
    steps: [
      {
        bodyKey: "dashboard-sop-date-1",
        bodyFallback:
          "Click the date button at the top of the dashboard. It shows the range currently in use, such as Jun 7, 2026 – Jun 13, 2026."
      },
      {
        bodyKey: "dashboard-sop-date-2",
        bodyFallback:
          "Choose a quick preset on the left, such as Today, Last 7 Days, This Month, or Year to Date.",
        imageId: "date-range"
      },
      {
        bodyKey: "dashboard-sop-date-3",
        bodyFallback:
          "Or pick start and end dates on the calendar, then click Apply."
      },
      {
        bodyKey: "dashboard-sop-date-4",
        bodyFallback:
          "The green or red trend text on each card compares your results with the previous period of the same length."
      }
    ]
  },
  {
    id: "summary-cards",
    titleKey: "dashboard-sop-summary-title",
    titleFallback: "Read the summary cards",
    introKey: "dashboard-sop-summary-intro",
    introFallback:
      "The top row gives you the money picture at a glance. These cards are the fastest way to see how sales and tax are tracking for the period you selected.",
    steps: [
      {
        bodyKey: "dashboard-sop-summary-1",
        bodyFallback:
          "Total Revenue shows how much you sold in the selected period."
      },
      {
        bodyKey: "dashboard-sop-summary-2",
        bodyFallback: "VAT to pay shows the tax amount linked to those sales."
      },
      {
        bodyKey: "dashboard-sop-summary-3",
        bodyFallback:
          "Net Revenue and Net Profit Margin help you see what is left after tax and how healthy your margin is."
      },
      {
        bodyKey: "dashboard-sop-summary-4",
        bodyFallback:
          "The Subscription section shows your plan status, last payment, and next billing date."
      }
    ]
  },
  {
    id: "orders-section",
    titleKey: "dashboard-sop-orders-title",
    titleFallback: "Orders section",
    introKey: "dashboard-sop-orders-intro",
    introFallback:
      "The Orders section shows how many sales you made and whether they were completed and paid. Use it to spot slow periods or unpaid invoices.",
    steps: [
      {
        bodyKey: "dashboard-sop-orders-1",
        bodyFallback:
          "Scroll to the Orders panel and expand it if it is collapsed."
      },
      {
        bodyKey: "dashboard-sop-orders-2",
        bodyFallback:
          "Review the cards for total orders, orders in the period, paid or completed orders, and total invoices.",
        imageId: "orders-section"
      },
      {
        bodyKey: "dashboard-sop-orders-3",
        bodyFallback:
          "Use the Orders by status chart to see how many orders are draft, pending payment, completed, or cancelled."
      },
      {
        bodyKey: "dashboard-sop-orders-4",
        bodyFallback:
          "Use the Invoices by status chart to see unpaid, partially paid, and paid invoices."
      }
    ]
  },
  {
    id: "products-section",
    titleKey: "dashboard-sop-products-title",
    titleFallback: "Products section",
    introKey: "dashboard-sop-products-intro",
    introFallback:
      "The Products section shows your catalog size and stock health. It helps you see how much inventory you have and how much has already sold.",
    steps: [
      {
        bodyKey: "dashboard-sop-products-1",
        bodyFallback: "Open the Products panel below Orders."
      },
      {
        bodyKey: "dashboard-sop-products-2",
        bodyFallback:
          "Total Products counts your catalog items. Total Product Items counts the individual stock lines across stores.",
        imageId: "products-section"
      },
      {
        bodyKey: "dashboard-sop-products-3",
        bodyFallback:
          "The Product items by state chart shows how many items are in stock, sold, or in another state. Hover over a bar to see the exact count.",
        imageId: "product-items-chart"
      },
      {
        bodyKey: "dashboard-sop-products-4",
        bodyFallback:
          "The Products by status chart shows how many catalog products are active or inactive."
      }
    ]
  },
  {
    id: "other-sections",
    titleKey: "dashboard-sop-other-title",
    titleFallback: "Other dashboard sections",
    introKey: "dashboard-sop-other-intro",
    introFallback:
      "Further down the page you will find more panels for the rest of your business. Each one follows the same pattern: summary cards plus a chart.",
    steps: [
      {
        bodyKey: "dashboard-sop-other-1",
        bodyFallback:
          "Categories: see how many product groups you have and how many are active.",
        imageId: "categories-section"
      },
      {
        bodyKey: "dashboard-sop-other-2",
        bodyFallback:
          "Stores: see how many locations you run and their status, such as active or pending.",
        imageId: "stores-section"
      },
      {
        bodyKey: "dashboard-sop-other-3",
        bodyFallback:
          "Employees: see your team size, who is active, and how roles are spread across the business.",
        imageId: "employees-section"
      },
      {
        bodyKey: "dashboard-sop-other-4",
        bodyFallback:
          "Customers: see how many customers you serve, how many are new in the period, and who still has unpaid invoices.",
        imageId: "customers-section"
      },
      {
        bodyKey: "dashboard-sop-other-5",
        bodyFallback:
          "Suppliers: see how many vendors you work with and whether they are active.",
        imageId: "suppliers-section"
      },
      {
        bodyKey: "dashboard-sop-other-6",
        bodyFallback:
          "Click the arrow on any panel heading to expand or collapse that section when you want a cleaner view."
      }
    ]
  }
];
